import React, { useState, useEffect } from 'react';
import { Button, Card, Form, Nav } from 'react-bootstrap';
import { database } from '../firebase';
import { ref, onValue, set, get } from 'firebase/database';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation } from 'react-router-dom';

const VeGaSettings = () => {
  const today = new Date().toISOString().split('T')[0];
  const [rolloverDate, setRolloverDate] = useState(today);
  const [tempRolloverDate, setTempRolloverDate] = useState(today);
  const [veloGaPrice, setVeloGaPrice] = useState('260');
  const [tempPrice, setTempPrice] = useState('260');
  const [singleTicketPrice, setSingleTicketPrice] = useState('7.2');
  const [tempSingleTicketPrice, setTempSingleTicketPrice] = useState('7.2');

  const [ownedBikeIds, setOwnedBikeIds] = useState<string[]>([]);
  const { currentUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (!currentUser) return;

    const setupFirebaseListeners = async () => {
      const dateRef = ref(database, `vega/${currentUser.uid}/rollover_date`);
      const dateUnsubscribe = onValue(dateRef, (snapshot) => {
        const data = snapshot.val();
        if (data === null) {
          // If no date is set in the database, set it to today
          set(dateRef, today);
          setRolloverDate(today);
          if (!tempRolloverDate) {
            setTempRolloverDate(today);
          }
        } else {
          setRolloverDate(data);
          // Only update temp value on initial load or if it matches current value
          if (!tempRolloverDate || tempRolloverDate === rolloverDate) {
            setTempRolloverDate(data);
          }
        }
      });

      const priceRef = ref(database, `vega/${currentUser.uid}/velo_ga_price`);
      const priceUnsubscribe = onValue(priceRef, (snapshot) => {
        const price = snapshot.val();
        if (price === null) {
          // If no price is set in the database, set it to 260
          set(priceRef, 260);
          setVeloGaPrice('260');
          if (!tempPrice) {
            setTempPrice('260');
          }
        } else {
          setVeloGaPrice(price.toString());
          // Only update temp value on initial load or if it matches current value
          if (!tempPrice || tempPrice === veloGaPrice) {
            setTempPrice(price.toString());
          }
        }
      });

      const singleTicketPriceRef = ref(database, `vega/${currentUser.uid}/price_single_ticket`);
      const singleTicketPriceUnsubscribe = onValue(singleTicketPriceRef, (snapshot) => {
        const price = snapshot.val();
        if (price === null) {
          // If no price is set in the database, set it to 7.2
          set(singleTicketPriceRef, 7.2);
          setSingleTicketPrice('7.2');
          if (!tempSingleTicketPrice) {
            setTempSingleTicketPrice('7.2');
          }
        } else {
          setSingleTicketPrice(price.toString());
          // Only update temp value on initial load or if it matches current value
          if (!tempSingleTicketPrice || tempSingleTicketPrice === singleTicketPrice) {
            setTempSingleTicketPrice(price.toString());
          }
        }
      });

      // Fetch owned bikes
      const ownedBikesRef = ref(database, `user_settings/${currentUser.uid}/ownedBikes`);
      const ownedBikesUnsubscribe = onValue(ownedBikesRef, (snapshot) => {
        const data = snapshot.val() || {};
        setOwnedBikeIds(Object.values(data));
      });

      return () => {
        dateUnsubscribe();
        priceUnsubscribe();
        singleTicketPriceUnsubscribe();
        ownedBikesUnsubscribe();
      };
    };

    const cleanup = setupFirebaseListeners();
    return () => {
      cleanup.then(cleanupFn => cleanupFn());
    };
  }, [currentUser, today, rolloverDate]);

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTempRolloverDate(event.target.value);
  };

  const handleSaveDate = async () => {
    if (!currentUser) return;
    
    try {
      await set(ref(database, `vega/${currentUser.uid}/rollover_date`), tempRolloverDate);
      setRolloverDate(tempRolloverDate);
    } catch (error) {
      console.error('Error updating rollover date:', error);
    }
  };

  const handlePriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty string and numbers
    if (value === '' || /^\d*$/.test(value)) {
      setTempPrice(value);
    }
  };

  const handleSavePrice = async () => {
    if (!currentUser) return;
    
    try {
      const numericPrice = parseInt(tempPrice) || 260;
      await set(ref(database, `vega/${currentUser.uid}/velo_ga_price`), numericPrice);
      setVeloGaPrice(numericPrice.toString());
    } catch (error) {
      console.error('Error updating Velo GA price:', error);
    }
  };

  const handleSingleTicketPriceChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    // Allow empty string, numbers, and decimal point
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setTempSingleTicketPrice(value);
    }
  };

  const handleSaveSingleTicketPrice = async () => {
    if (!currentUser) return;
    
    try {
      const numericPrice = parseFloat(tempSingleTicketPrice) || 7.2;
      await set(ref(database, `vega/${currentUser.uid}/price_single_ticket`), numericPrice);
      setSingleTicketPrice(numericPrice.toString());
    } catch (error) {
      console.error('Error updating Einzelfahrt price:', error);
    }
  };



  return (
    <div className="container">
      <h2 className="mb-4">VeGa Settings</h2>
      
      {/* Navigation Tabs */}
      <Nav variant="tabs" className="mb-4">
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega" 
            active={location.pathname === '/dashboard/vega'}
          >
            Dashboard
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega/rides" 
            active={location.pathname === '/dashboard/vega/rides'}
          >
            Fahrten
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link 
            as={Link} 
            to="/dashboard/vega/settings" 
            active={location.pathname === '/dashboard/vega/settings'}
          >
            Konfiguration
          </Nav.Link>
        </Nav.Item>
      </Nav>
      
      <div className="row mb-4">
        <div className="col-12">
          <Card className="mb-4">
            <Card.Body>
              <Card.Title style={{ color: '#1780a6' }}>Velo GA</Card.Title>
              <Form.Group>
                <Form.Label>Preis</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={tempPrice}
                    onChange={handlePriceChange}
                    placeholder="Enter price"
                  />
                  <Button onClick={handleSavePrice}>Save</Button>
                </div>
              </Form.Group>
              <div className="mt-2 text-muted">
                Current price: {veloGaPrice}
              </div>
              
              <Form.Group className="mt-3">
                <Form.Label>Preis einzelfahrt</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    value={tempSingleTicketPrice}
                    onChange={handleSingleTicketPriceChange}
                    placeholder="Enter single ticket price"
                  />
                  <Button onClick={handleSaveSingleTicketPrice}>Save</Button>
                </div>
              </Form.Group>
              <div className="mt-2 text-muted">
                Current single ticket price: {singleTicketPrice}
              </div>
              
              <Form.Group className="mt-3">
                <Form.Label>Rollover Date</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="date"
                    value={tempRolloverDate}
                    onChange={handleDateChange}
                  />
                  <Button onClick={handleSaveDate}>Save</Button>
                </div>
                <div className="mt-2 text-muted">
                  Current rollover date: {rolloverDate}
                </div>
              </Form.Group>
            </Card.Body>
          </Card>


        </div>
      </div>
    </div>
  );
};

export default VeGaSettings;
